import { useLiveStateManager } from '../useLiveStateManager';
import { useSendVmCommand } from './useSendVmCommand';
import { useStartRecorder } from './useStartRecorder';
import { useCallback } from 'react';

type BaseStep = {
  id: string;
  stepOrder: number;
};

type StepWithOperations = BaseStep & {
  operations: { id: string; order: number }[];
};

export function usePreviewFromLocation({ testContentId }: { testContentId: string }) {
  const { recorderState, vmStatus, updateLiveState } = useLiveStateManager();
  const sendVmCommand = useSendVmCommand();
  const startRecorder = useStartRecorder({ testContentId });

  return useCallback(
    ({ step, operation }: { step: BaseStep | StepWithOperations; operation?: { id: string; order: number } }) => {
      const targetOperation = operation ?? ('operations' in step ? step.operations.at(-1) : undefined);
      const operationOrder = targetOperation?.order ?? 0;
      if (vmStatus) {
        sendVmCommand({
          type: 'switch-mode',
          mode: 'preview',
          cursorStepId: recorderState?.stepId ?? '',
          cursorStepOrder: recorderState?.stepOrder ?? 0,
          endStepAndOp: {
            stepId: step.id,
            operationOrder,
          },
        });
        updateLiveState((prev) => ({
          ...prev,
          vmStatus: {
            state: 'ready',
            humanState: 'ready',
            aliasesToUrls: vmStatus?.aliasesToUrls,
          },
          recorderInitiator: { ...prev.recorderInitiator, executionType: 'preview' },
        }));
      } else {
        // Determine recorder location based on whether we have a specific operation
        const location =
          operation && targetOperation ?
            {
              type: 'operation-id' as const,
              stepId: step.id,
              order: step.stepOrder,
              operationId: targetOperation.id,
              // The + 1 is so that we make sure that this operation is included in the preview
              operationOrder: targetOperation.order + 1,
            }
          : {
              type: 'step-id' as const,
              stepId: step.id,
              order: step.stepOrder,
            };

        startRecorder({
          executionType: 'preview',
          location,
        });
      }
    },
    [vmStatus, recorderState?.stepId, recorderState?.stepOrder, sendVmCommand, updateLiveState, startRecorder],
  );
}
